import * as Cookies from 'js-cookie';
import { Dispatch } from 'react-redux';
import { parse } from 'query-string';

import { StoreThunk, LogoutStatusMessages } from './types';
import {
  setAuthData,
  setAuthError,
  routeUser,
  setRegistrationData,
  isAuthorized
} from './auth';
import { setEmailVerificationData } from './emailVerification';
import { BrandingData, StoreState, AuthErrorType, AuthData } from '../types';
import * as built from '../built';
import * as actions from '../actions';
import * as utils from '../utils';
import getAuthModule from '../auth';
import history from '../history';
import l10n from '@built/i18n';
import { initializeI18n } from './i18n';

export * from './auth';
export * from './user';
export * from './emailVerification';
export * from './toast';
export * from './snowplow';

export const SET_INITIALIZED = 'SET_INITIALIZED';
export const SET_BRANDING = 'SET_BRANDING';
export const CLEAR_ERRORS = 'CLEAR_ERRORS';

const authModule = getAuthModule();
const LOGOUT_URL_COOKIE = 'builtLogoutUrl';
const LOGOUT_STATUS_COOKIE = 'builtLogoutStatus';
const SSO_URL_REGEX = /^https?:\/\/.*\/sso\/([a-zA-z0-9]+)\/?\?code=(.*)$/;
const REGION = utils.getConfig('REACT_APP_REGION');
const USER_POOL_DOMAIN = utils.getConfig('REACT_APP_USER_POOL_DOMAIN_PREFIX');
const USER_POOL_LOGOUT_REGEX = new RegExp(
  `https:\/\/${USER_POOL_DOMAIN}\.auth\.${REGION}\.amazoncognito\.com\/logout`
);

/**
 * Initialize the application
 */
// TODO (abe) simplify this...
export function initialize(): StoreThunk {
  return (dispatch, getState) => {
    const params = parse(location.search);
    const hashParams = parse(location.hash);

    // Initialize language flag
    initializeI18n(l10n, dispatch);

    // if email is provided in query params, set it in the state
    // so other components can use it
    // LOGIN-175 - the email query param is also used for IMA
    // user flows. overriding the complete AuthData state here
    // will prevent IMA user migration
    if (params.email) {
      dispatch(
        setAuthData({
          ...getState().authData,
          userProvidedEmail: params.email
        })
      );
    }

    getBranding(params, dispatch)
      .then(() => {
        if (location.pathname === '/maintenance') {
          // When a BAPI response tells us we're in maintenance mode, we
          // land at this route and need to react accordingly
          return handleMaintenanceMode();
        } else if (location.pathname === '/logout') {
          return handleLogout(dispatch);
        } else if (location.pathname === '/verify_email') {
          return handleEmailVerification(params, dispatch);
        } else if (location.pathname.indexOf('/sso') === 0) {
          return handleAuthCodeExchange(params).then(authData => {
            dispatch(setAuthData(authData));
            return authData;
          });
        } else if (hashParams.id_token && hashParams.access_token) {
          return handleHashParams(hashParams).then(authData => {
            dispatch(setAuthData(authData));
            return authData;
          });
        } else if (params.registrationToken) {
          // If there is a registration token in the URL, we need to
          // retrieve user information for the new user
          return handleRegistrationToken(params.registrationToken).then(
            data => {
              dispatch(setAuthData(data));
              dispatch(setRegistrationData(data));
              return data;
            }
          );
        } else if (getState().authData) {
          // Check to see if there's already auth data in the store
          return Promise.resolve(getState().authData).then(authData => {
            dispatch(setAuthData(authData));
            return authData;
          });
        } else {
          // Attempt to load auth data from local/cookie storage
          return handleExistingSession()
            .then(authData => {
              dispatch(setAuthData(authData));
              return authData;
            })
            .catch(() => {
              // LOGIN-169
              // catch errors while reloading a user's session and
              // return undefined. The cognito library handles
              // clearing out the relevant local storage bits for us
              return undefined;
            });
        }
      })
      .then(authData => {
        if (!authData) {
          return undefined;
        }
        // inject isImaUser into authData
        const data = {
          ...authData,
          isImaUser: params.ima === '1'
        };
        dispatch(setAuthData(data));
        return data;
      })
      .catch(error => dispatch(setAuthError(error)))
      .then(() => dispatch(routeUser()))
      .then(result => dispatch(setInitialized(result)));
  };
}

export interface SetInitialized {
  type: typeof SET_INITIALIZED;
  initialized: boolean;
}

/**
 * Sets the initialized flag, indicating that the app has or has not been initialized
 */
export function setInitialized(initialized = true): SetInitialized {
  return {
    type: SET_INITIALIZED,
    initialized
  };
}

export interface SetBranding {
  type: typeof SET_BRANDING;
  branding?: BrandingData;
}

/**
 * Sets branding info for the app
 */
export function setBranding(branding?: BrandingData): SetBranding {
  return {
    type: SET_BRANDING,
    branding
  };
}

export interface ClearErrors {
  type: typeof CLEAR_ERRORS;
}

/**
 * Clears application errors
 */
export function clearErrors(): ClearErrors {
  return { type: CLEAR_ERRORS };
}

/**
 * Retrieves branding info from the Built API and then stores it in the application
 * state
 */
function getBranding(
  params: { [key: string]: string },
  dispatch: Dispatch<StoreState>
) {
  // LOGIN-185 pull branding data only for non-id subdomains
  if (location.hostname.match(/^id[-.]/)) {
    return Promise.resolve();
  }

  return built.getBranding().then(resp => {
    dispatch(
      setBranding({
        externalLoginUrl: resp.ssoRedirectUrl,
        logoFile: resp.logoFile,
        headline: resp.headline,
        subtext: resp.subtext,
        actionColor: `#${resp.actionColor}`,
        primaryColor: `#${resp.primaryColor}`,
        secondaryColor: `#${resp.secondaryColor}`
      })
    );
  });
}

/**
 * Handle redirection back to our app with the various auth tokens
 * in the fragment portion of the URL
 */
function handleHashParams(params: { [key: string]: string }) {
  const accessToken = utils.parseJWTToken(params.access_token);
  const email = accessToken.email || accessToken.username;
  const promise = Promise.resolve().then(() => ({
    email,
    username: email,
    idToken: params.id_token,
    accessToken: params.access_token,
    refreshToken: params.refresh_token,
    federated: true
  }));

  // handle user account association if we have ID and registration tokens
  // FIXME (abe) this will likely need updating for inbound OIDC tokens
  if (params.id_token && params.registration_token) {
    const idToken = params.id_token;
    const registrationToken = params.registration_token;

    // associate the user with Built API
    return built
      .associateUserAccount(idToken, registrationToken)
      .then(() => promise);
  }

  return promise;
}

/**
 * This method handles verifying the user's email address and
 * verification code with Built API
 */
function handleEmailVerification(
  params: { [key: string]: string },
  dispatch: Dispatch<StoreState>
) {
  const { changeId, changeToken } = params;

  if (changeId && changeToken) {
    // log the user out first because we'll need to log them again
    // during the email verification flow
    dispatch(actions.logout());

    // next, we check with BAPI to confirm that the verification
    // bits provided are valid
    return (
      built
        .verifyEmailVerificationToken(changeId, changeToken)
        .then(data => {
          // email verification success; return the appropriate AuthData
          // state change
          dispatch(
            setEmailVerificationData({
              changeId,
              changeToken,
              ...data
            })
          );
          return data;
        })
        // trigger an AuthError state change if an error occurred
        .catch(() => {
          const err = utils.getAuthError(
            AuthErrorType.EMAIL_VERIFICATION_ERROR
          );
          dispatch(
            setEmailVerificationData({
              error: err
            })
          );
          return Promise.reject({});
        })
    );
  }

  // if email and code was not provided, do nothing and act like
  // it didn't happen
  return Promise.resolve();
}

/**
 * Call the Built register endpoint, which will return the user data for this registration
 * token
 */
function handleRegistrationToken(registrationToken: string) {
  return authModule
    .logout()
    .then(() => built.register(registrationToken))
    .then(({ data }) => {
      // set state to collect user's initial password for registration
      data.newPasswordRequired = true;
      return data;
    })
    .catch(error => {
      return Promise.reject({
        title: error.error,
        type: AuthErrorType.REGISTRATION_ERROR
      });
    });
}

/**
 * Sets the app state to handle maintenance mode
 */
function handleMaintenanceMode() {
  return Promise.resolve({ maintenanceMode: true });
}

/**
 * LOGIN-194:
 * Check for the existence of a built logout URL cookie, and
 * check if it's an external logout URL. This particular case
 * happens when an SSO user authenticates and navigates back
 * to the login app and is presented with the welcome back
 * view. If they attempt to logout on that view, they need
 * to logout in the auth provider as well.
 */
function attemptLogoutRedirect(): boolean {
  const logoutUrl = Cookies.get(LOGOUT_URL_COOKIE);
  const isLogoutUrl = logoutUrl && USER_POOL_LOGOUT_REGEX.test(logoutUrl);

  // always remove this cookie
  Cookies.remove(LOGOUT_URL_COOKIE, utils.getCookieOptions());

  // if we determine it's a valid logout URL, redirect and return
  // true to shortcircuit any additional logic
  if (isLogoutUrl) {
    window.location.href = logoutUrl!;
    return true;
  }
  return false;
}

export function handleLogout(dispatch: Dispatch<StoreState>) {
  const cookieOptions = utils.getCookieOptions();

  // bail out if we're redirecting
  if (attemptLogoutRedirect()) {
    return;
  }

  // log the user out
  dispatch(actions.logout());

  // if we have a logout status, we translate it to a human readable message
  // and then clear the cookie to not bother the user again
  const logoutStatus = getLogoutStatusMessage();

  Cookies.remove(LOGOUT_STATUS_COOKIE, cookieOptions);

  // return the logout status so we can update state
  return Promise.resolve({
    logoutStatus
  });
}

function getLogoutStatusMessage(): string {
  // look for logout status cookie and update state
  let logoutStatus = Cookies.get(LOGOUT_STATUS_COOKIE);

  if (logoutStatus === undefined) {
    return '';
  }

  // currently, console will stringify and URI encode the
  // logout status, but this could change in the future
  // so we handle JSON encoded and vanilla encoded strings
  try {
    logoutStatus = JSON.parse(decodeURIComponent(logoutStatus));
  } catch (e) {
    logoutStatus = decodeURIComponent(logoutStatus);
  }

  // remap logout status to a real message or empty string
  return LogoutStatusMessages[logoutStatus!] || '';
}

/**
 * @desc Handles exchanging the auth code provided by Cognito
 * during a SSO request for JWT tokens.
 *
 * @param params key/value pairs of query arguments
 */
function handleAuthCodeExchange(params: {
  error?: string;
  error_description?: string;
}) {
  // handle saml errors that are provided in the redirect
  if (params.error || params.error_description) {
    const err = utils.getAuthError(
      AuthErrorType.SAML_RESPONSE_ERROR,
      params.error_description
    );
    history.push({ pathname: '/', search: undefined });
    return Promise.reject(err);
  }

  const ssoMatch = location.href.match(SSO_URL_REGEX);
  if (!ssoMatch) {
    history.push({ pathname: '/', search: undefined });
    return Promise.resolve(undefined);
  }

  // exchange and return our tokens for AuthData state
  const clientId = ssoMatch[1];
  const authCode = ssoMatch[2];
  const redirectUrl = `https://${location.host}${location.pathname}`;

  return authModule
    .logout()
    .then(() => authModule.exchangeAuthCode(clientId, authCode, redirectUrl))
    .catch(error => {
      history.push({ pathname: '/', search: undefined });
      const err = utils.getAuthError(AuthErrorType.SAML_RESPONSE_ERROR);
      return Promise.reject(err);
    });
}

/**
 * Attempts to load an existing user and session from Cognito storage
 * and returns valid AuthData
 */
async function handleExistingSession(): Promise<AuthData> {
  const defaultClientId = utils.getConfig('REACT_APP_CLIENT_ID');
  const clientId = utils.getClientIdFromCookies() || defaultClientId;
  const userSession = await authModule.getUser(clientId);
  const authData = await authModule.getAuthData(
    userSession.user,
    userSession.session
  );

  return {
    ...authData,
    welcomeBack: authData && isAuthorized(authData),
    ssoClientId: clientId !== defaultClientId ? clientId : undefined
  };
}



// WEBPACK FOOTER //
// ./src/actions/index.ts